"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Demo data
// ============================================================================

type CloserKey = 1 | 2 | 3;
type Mode = "round_robin" | "load_balanced" | "manual";

type SlotState = "free" | "busy";

type Slot = {
  day: 1 | 2 | 3 | 4 | 5;
  hour: 10 | 11 | 14 | 15;
  state: SlotState;
  closer: CloserKey | null;
};

// 5 days × 4 time-slots = 20 cells. ~12 free / ~8 busy, mixed across closers.
const SLOTS: Slot[] = [
  // Mo
  { day: 1, hour: 10, state: "free", closer: 1 },
  { day: 1, hour: 11, state: "busy", closer: null },
  { day: 1, hour: 14, state: "free", closer: 3 },
  { day: 1, hour: 15, state: "free", closer: 2 },
  // Di
  { day: 2, hour: 10, state: "busy", closer: null },
  { day: 2, hour: 11, state: "free", closer: 1 },
  { day: 2, hour: 14, state: "free", closer: 2 },
  { day: 2, hour: 15, state: "busy", closer: null },
  // Mi
  { day: 3, hour: 10, state: "free", closer: 3 },
  { day: 3, hour: 11, state: "busy", closer: null },
  { day: 3, hour: 14, state: "free", closer: 1 },
  { day: 3, hour: 15, state: "free", closer: 2 },
  // Do
  { day: 4, hour: 10, state: "busy", closer: null },
  { day: 4, hour: 11, state: "free", closer: 3 },
  { day: 4, hour: 14, state: "busy", closer: null },
  { day: 4, hour: 15, state: "free", closer: 1 },
  // Fr
  { day: 5, hour: 10, state: "free", closer: 2 },
  { day: 5, hour: 11, state: "free", closer: 1 },
  { day: 5, hour: 14, state: "busy", closer: null },
  { day: 5, hour: 15, state: "free", closer: 3 },
];

const HOURS: Slot["hour"][] = [10, 11, 14, 15];
const DAYS: Slot["day"][] = [1, 2, 3, 4, 5];
const FREE_INDEXES = SLOTS.map((s, i) => (s.state === "free" ? i : -1)).filter(
  (i) => i >= 0
);

const CLOSER_INITIALS: Record<CloserKey, string> = {
  1: "AS",
  2: "MB",
  3: "LR",
};

const ROTATION_MS = 4500;
const BOOKED_SLOT_INDEX = SLOTS.findIndex(
  (s) => s.day === 2 && s.hour === 11 && s.state === "free"
);

// ============================================================================
// Root Section
// ============================================================================

export const SchedulingSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <BookingScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("scheduling");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <CalendarIcon className="h-3.5 w-3.5" />
        Scheduling
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Booking Scene
// ============================================================================

const BookingScene = () => {
  const t = useTranslations("scheduling");
  const [mode, setMode] = React.useState<Mode>("round_robin");
  const [bookingPulse, setBookingPulse] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);

  // Auto-booking animation: pulse → confirm → reset
  React.useEffect(() => {
    let timers: ReturnType<typeof setTimeout>[] = [];
    const cycle = () => {
      setConfirmed(false);
      setBookingPulse(true);
      timers.push(
        setTimeout(() => {
          setBookingPulse(false);
          setConfirmed(true);
        }, 1400)
      );
      timers.push(setTimeout(cycle, ROTATION_MS));
    };
    cycle();
    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[600px]">
        <SetterPane mode={mode} onModeChange={setMode} />
        <CalendarPane
          mode={mode}
          bookingPulse={bookingPulse}
          confirmed={confirmed}
        />
      </div>
      <div className="border-t border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] text-neutral-500">{t("footer_meta")}</span>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Setter Pane (left)
// ----------------------------------------------------------------------------

const SetterPane = ({
  mode,
  onModeChange,
}: {
  mode: Mode;
  onModeChange: (m: Mode) => void;
}) => {
  const t = useTranslations("scheduling");

  return (
    <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/40 p-5 flex flex-col">
      {/* Lead chip */}
      <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-3 mb-5">
        <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">
          {t("lead_panel_label")}
        </div>
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-500/15 text-red-300 text-[11px] font-semibold ring-1 ring-red-500/30">
            {t("lead_first_name")[0]}
            {t("lead_last_name")[0]}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-neutral-100 truncate">
              {t("lead_first_name")} {t("lead_last_name")}
            </div>
            <div className="text-[11px] text-neutral-500 truncate">
              {t("lead_role")} · {t("lead_company")}
            </div>
          </div>
        </div>
      </div>

      {/* Routing-mode toggle */}
      <div className="mb-5">
        <div className="text-[10px] font-semibold tracking-wider text-neutral-500 uppercase mb-2">
          {t("mode_label")}
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-neutral-900 border border-neutral-800">
          {(["round_robin", "load_balanced", "manual"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onModeChange(m)}
              className={cn(
                "flex-1 text-[10px] font-medium px-2 py-1.5 rounded-md transition-colors whitespace-nowrap",
                mode === m
                  ? "bg-red-500/15 text-red-200 ring-1 ring-red-500/30"
                  : "text-neutral-500 hover:text-neutral-300"
              )}
            >
              {t(`mode_${m}`)}
            </button>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-neutral-500 leading-relaxed">
          {t(`mode_meta_${mode}`)}
        </div>
      </div>

      {/* Closer pool */}
      <div className="flex items-center gap-1.5 mb-2">
        <BoltIcon className="h-3.5 w-3.5 text-red-500" />
        <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
          {t("pool_label")}
        </span>
      </div>
      <div className="text-[10px] text-neutral-500 mb-3">{t("pool_meta")}</div>

      <ul className="space-y-2 flex-1">
        {([1, 2, 3] as CloserKey[]).map((key) => (
          <CloserCard
            key={key}
            closerKey={key}
            isNext={mode === "round_robin" && key === 1}
            isFavored={mode === "load_balanced" && key === 2}
          />
        ))}
      </ul>
    </div>
  );
};

const CloserCard = ({
  closerKey,
  isNext,
  isFavored,
}: {
  closerKey: CloserKey;
  isNext: boolean;
  isFavored: boolean;
}) => {
  const t = useTranslations("scheduling");
  const highlighted = isNext || isFavored;
  return (
    <li
      className={cn(
        "rounded-xl border p-3 transition-colors",
        highlighted
          ? "border-red-500/50 bg-neutral-900"
          : "border-neutral-800 bg-neutral-900/40"
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
            highlighted
              ? "bg-red-500/20 text-red-200 ring-1 ring-red-500/50"
              : "bg-neutral-800 text-neutral-400 ring-1 ring-neutral-700"
          )}
        >
          {CLOSER_INITIALS[closerKey]}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-neutral-100 truncate">
              {t(`closer_${closerKey}_name`)}
            </span>
            {isNext && (
              <span className="text-[9px] uppercase tracking-wider font-semibold text-red-200 bg-red-500/20 ring-1 ring-red-500/40 px-1.5 py-0.5 rounded">
                {t("next_label")}
              </span>
            )}
          </div>
          <div className="text-[11px] text-neutral-500 truncate mt-0.5">
            {t(`closer_${closerKey}_role`)}
          </div>
        </div>
      </div>
    </li>
  );
};

// ----------------------------------------------------------------------------
// Calendar Pane (right)
// ----------------------------------------------------------------------------

const CalendarPane = ({
  mode,
  bookingPulse,
  confirmed,
}: {
  mode: Mode;
  bookingPulse: boolean;
  confirmed: boolean;
}) => {
  const t = useTranslations("scheduling");

  return (
    <div className="lg:col-span-8 relative overflow-hidden p-5 bg-neutral-950 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <CalendarIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t("calendar_label")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-emerald-400 bg-emerald-950/60 border border-emerald-900 px-1.5 py-0.5 rounded">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {t("live_label")}
          </span>
          <span className="text-[10px] text-neutral-500">{t("calendar_meta")}</span>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-[44px_repeat(5,minmax(0,1fr))] gap-2 mb-2">
        <div />
        {DAYS.map((d) => (
          <div
            key={d}
            className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold text-center"
          >
            {t(`day_${d}`)}
          </div>
        ))}
      </div>

      {/* Slot grid */}
      <ul className="flex-1 space-y-2">
        {HOURS.map((h) => (
          <li
            key={h}
            className="grid grid-cols-[44px_repeat(5,minmax(0,1fr))] gap-2 items-stretch"
          >
            <div className="flex items-center justify-end pr-2 text-[11px] font-mono tabular-nums text-neutral-500">
              {h.toString().padStart(2, "0")}:00
            </div>
            {DAYS.map((d) => {
              const idx = SLOTS.findIndex((s) => s.day === d && s.hour === h);
              const slot = SLOTS[idx];
              const isBookingTarget = idx === BOOKED_SLOT_INDEX;
              return (
                <SlotCell
                  key={`${d}-${h}`}
                  slot={slot}
                  pulsing={isBookingTarget && bookingPulse}
                  booked={isBookingTarget && confirmed}
                />
              );
            })}
          </li>
        ))}
      </ul>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[10px] text-neutral-500">
        <LegendDot
          color="border-red-500/60 bg-red-500/10"
          label={t("slot_legend_free")}
        />
        <LegendDot
          color="border-neutral-800 bg-neutral-900"
          label={t("slot_legend_busy")}
        />
        <LegendDot
          color="border-emerald-500 bg-emerald-500/20"
          label={t("slot_legend_booked")}
        />
      </div>

      {/* Confirmation toast */}
      <div className="mt-4 min-h-[64px]">
        <AnimatePresence>
          {confirmed && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="rounded-xl border border-emerald-900 bg-emerald-950/40 p-3 flex items-center gap-3"
            >
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 ring-1 ring-emerald-500/40 text-emerald-300">
                <CheckIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-emerald-200 truncate">
                  {t("booked_status")}
                </div>
                <div className="text-[11px] text-emerald-400/80 truncate mt-0.5">
                  {t("confirmation_meta")}
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-300 bg-emerald-900/60 border border-emerald-800 rounded px-1.5 py-0.5 hidden sm:inline-block">
                {mode === "round_robin"
                  ? t("mode_round_robin")
                  : mode === "load_balanced"
                  ? t("mode_load_balanced")
                  : t("mode_manual")}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const SlotCell = ({
  slot,
  pulsing,
  booked,
}: {
  slot: Slot;
  pulsing: boolean;
  booked: boolean;
}) => {
  if (slot.state === "busy") {
    return (
      <div className="relative h-10 rounded-md border border-neutral-800 bg-neutral-900/60 flex items-center justify-center">
        <span className="text-[10px] text-neutral-600 line-through font-mono">
          —
        </span>
      </div>
    );
  }
  // free or booked
  return (
    <div
      className={cn(
        "relative h-10 rounded-md border flex items-center justify-center transition-colors",
        booked
          ? "border-emerald-500 bg-emerald-500/20 text-emerald-200"
          : "border-red-500/40 bg-red-500/5 text-red-200 hover:bg-red-500/10"
      )}
    >
      {pulsing && (
        <motion.span
          className="absolute inset-0 rounded-md ring-2 ring-red-500"
          initial={{ opacity: 0.8, scale: 1 }}
          animate={{ opacity: 0, scale: 1.15 }}
          transition={{ duration: 1.2, ease: "easeOut", repeat: Infinity }}
        />
      )}
      {booked ? (
        <CheckIcon className="h-3.5 w-3.5" />
      ) : (
        <span className="text-[10px] font-semibold">
          {slot.closer ? CLOSER_INITIALS[slot.closer] : ""}
        </span>
      )}
    </div>
  );
};

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={cn("h-3 w-3 rounded-sm border", color)} />
    <span>{label}</span>
  </span>
);

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconRoundRobin /> },
  { key: "t2", icon: <IconBalance /> },
  { key: "t3", icon: <IconCalendarSync /> },
  { key: "t4", icon: <IconBell /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("scheduling");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 10h18" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 12l5 5L20 6" />
    </svg>
  );
}

function IconRoundRobin() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <path d="M21 4v5h-5" />
    </svg>
  );
}

function IconBalance() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M12 4v16M4 8h16" />
      <path d="M4 8l-2 6h6zM20 8l-2 6h6z" />
    </svg>
  );
}

function IconCalendarSync() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 10h18" />
      <path d="M9 15a3 3 0 0 1 5-2.2M15 17a3 3 0 0 1-5 2.2" />
    </svg>
  );
}

function IconBell() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9z" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </svg>
  );
}
